import { useWorkflowGraphState } from "#src/batteries-included-components/Graph/WorkflowGraph/WorkflowGraphCanvas/WorkflowGraphStore";
import { useListReportingGroups } from "#src/components/hooks/adapters/useReportingGroups";
import {
  Button,
  CheckboxInput,
  DateSelectorInputBare,
  DateValueOrRange,
  FilterBuilder,
  FilterBuilderFieldsType,
  Form,
  FormLabel,
  RadioInput,
  Skeleton,
  useForm,
  VariableTextInputBare,
} from "@validereinc/common-components";
import {
  AssetType,
  AttributeDataType,
  RunDRVCActionSchema,
} from "@validereinc/domain";
import { yearMonthFormatter, yearMonthToDate } from "@validereinc/utilities";
import classNames from "classnames/bind";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  RunDRVCFormValuesType,
  useWorkflowConfigurationFormStore,
} from "../WorkflowConfigurationFormStore";
import styles from "./WorkflowConfigurationRunDRVCDetails.module.scss";

const cx = classNames.bind(styles);

const monthModeOptions = [
  { label: "Specific Months", value: "specific" },
  { label: "Month Range", value: "range" },
];

const isVariableValue = (value: string): boolean => {
  return value.includes("$");
};

const useBuildDRVCFilterFields = () => {
  const { data: reportingGroupsResponse, isLoading: isLoadingReportingGroups } =
    useListReportingGroups();

  const reportingGroupOptions = useMemo(() => {
    return reportingGroupsResponse?.data?.map((group) => group.id) ?? [];
  }, [reportingGroupsResponse?.data]);

  const filterFields: FilterBuilderFieldsType = useMemo(
    () => ({
      reporting_group_id: {
        data_type: AttributeDataType.PICK_LIST,
        display_name: "Reporting Group",
        pick_list_options: reportingGroupOptions,
      },
      asset_type: {
        data_type: AttributeDataType.PICK_LIST,
        display_name: "Asset Type",
        pick_list_options: Object.values(AssetType),
      },
      "facility.id": {
        data_type: AttributeDataType.STRING,
        display_name: "Facility ID",
      },
      "equipment.facility.id": {
        data_type: AttributeDataType.STRING,
        display_name: "Facility ID of Equipment",
      },
    }),
    [reportingGroupOptions]
  );

  return { filterFields, isLoading: isLoadingReportingGroups };
};

const WorkflowConfigurationRunDRVCDetails = ({
  stepId,
}: {
  stepId: string;
}) => {
  const [specificMonths, setSpecificMonths] = useState<string[]>([]);
  const { setRunDRVC, setRunDRVCIsValid, getRunDRVCFormValues } =
    useWorkflowConfigurationFormStore();

  const { getAvailableStepVariables } = useWorkflowGraphState();
  const availableVariables = getAvailableStepVariables(stepId);

  const storeValues = getRunDRVCFormValues();
  const [filter, setFilter] = useState(storeValues?.filter ?? {});

  const form = useForm<RunDRVCFormValuesType>({
    values: storeValues,
  });
  const formValues = form.watch();
  const monthMode = formValues.month_mode;

  const { filterFields, isLoading } = useBuildDRVCFilterFields();

  const [monthInputTypes, setMonthInputTypes] = useState<
    Array<"variable" | "date">
  >(() => {
    const initialTypes = (storeValues?.specific_months ?? [""]).map((value) =>
      isVariableValue(value) ? "variable" : "date"
    );
    return initialTypes.length > 0 ? initialTypes : ["date"];
  });

  useEffect(() => {
    if (
      Array.isArray(storeValues?.specific_months) &&
      storeValues?.specific_months.length > 0
    ) {
      setSpecificMonths(storeValues?.specific_months);
    }
  }, [JSON.stringify(storeValues?.specific_months)]);

  const finalFormValues = useMemo(() => {
    const baseValues = {
      overwrite_all_values: formValues.overwrite_all_values,
      seed_records: true,
      filter,
      month_mode: formValues.month_mode,
    };

    if (formValues.month_mode === "specific") {
      return {
        ...baseValues,
        specific_months: specificMonths.filter((m) => m.trim() !== ""),
        fill_year_months: false,
      };
    } else {
      return {
        ...baseValues,
        start_month: formValues.start_month,
        end_month: formValues.end_month,
        fill_year_months: true,
      };
    }
  }, [JSON.stringify(formValues), filter, specificMonths]);

  useEffect(() => {
    setRunDRVC(finalFormValues);
  }, [JSON.stringify(finalFormValues), setRunDRVC]);

  useEffect(() => {
    setRunDRVCIsValid(form.formState.isValid && Object.keys(filter).length > 0);
  }, [form.formState.isValid, filter, setRunDRVCIsValid]);

  // Update filter state when switching between steps or when store values change
  useEffect(() => {
    setFilter(storeValues?.filter ?? {});
  }, [JSON.stringify(storeValues?.filter), stepId]);

  const updateMonth = useCallback((index: number, value: string) => {
    setSpecificMonths((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  }, []);

  const handleDateChange = useCallback(
    (index: number, date: Date | undefined) => {
      if (date) {
        updateMonth(index, yearMonthFormatter(date));
      } else {
        updateMonth(index, "");
      }
    },
    [updateMonth]
  );
  const handleRangeDateChange = useCallback(
    (field: "start" | "end", date: Date | undefined) => {
      if (date) {
        form.setValue(
          field === "start" ? "start_month" : "end_month",
          yearMonthFormatter(date)
        );
      } else {
        form.setValue(field === "start" ? "start_month" : "end_month", "");
      }
    },
    [form]
  );

  const toggleInputType = useCallback(
    (index: number) => {
      setMonthInputTypes((prev) => {
        const updated = [...prev];
        updated[index] = updated[index] === "variable" ? "date" : "variable";
        return updated;
      });
      // Clear value when switching input type
      updateMonth(index, "");
    },
    [updateMonth]
  );

  const addMonth = useCallback(() => {
    setSpecificMonths((prev) => [...prev, ""]);
    setMonthInputTypes((prev) => [...prev, "date"]);
  }, []);

  const removeMonth = useCallback((index: number) => {
    setSpecificMonths((prev) =>
      prev.length > 1 ? prev.filter((_, i) => i !== index) : prev
    );
    setMonthInputTypes((prev) =>
      prev.length > 1 ? prev.filter((_, i) => i !== index) : prev
    );
  }, []);

  return (
    <Skeleton
      className={cx("container")}
      isLoading={isLoading}
    >
      <Form
        {...form}
        style={{ maxWidth: 432 }}
      >
        <CheckboxInput
          name={RunDRVCActionSchema.keyof().Enum.overwrite_all_values}
          label="Overwrite All Values"
          description="When enabled, all existing record values will be overwritten with the default configuration values, even if they already have data."
          isLabelShown={false}
          isFluid
        />

        <RadioInput
          name="month_mode"
          label="Month Selection Mode"
          options={monthModeOptions}
          className={cx("month-mode-input")}
          valueKey="value"
          labelKey="label"
        />

        {monthMode === "specific" && (
          <div className={cx("specific-months-section")}>
            <label className={cx("section-label")}>Specific Months</label>
            <div className={cx("months-list")}>
              {specificMonths.map((month, index) => (
                <div
                  key={index}
                  className={cx("month-input-row")}
                >
                  <div className={cx("month-input")}>
                    {monthInputTypes[index] === "variable" ? (
                      <VariableTextInputBare
                        name={`specific_months.${index}`}
                        value={month}
                        onChange={(value) => {
                          updateMonth(index, value);
                        }}
                        placeholder="$VARIABLE"
                        suggestions={availableVariables}
                        type="text"
                        className={cx("variable-input")}
                      />
                    ) : (
                      <DateSelectorInputBare
                        name={`specific_months.${index}`}
                        variant="month"
                        value={yearMonthToDate(month)}
                        onChange={(date: DateValueOrRange | undefined) => {
                          if (typeof date === "undefined") return;
                          const singleDateValue =
                            date instanceof Date ? date : date.from;
                          handleDateChange(index, singleDateValue);
                        }}
                        placeholder="Select month"
                        className={cx("date-input")}
                        isRange={false}
                        isArraySyntax={false}
                      />
                    )}
                  </div>

                  <Button
                    type="button"
                    variant="ghost"
                    size="small"
                    icon="swap"
                    onClick={() => toggleInputType(index)}
                    className={cx("toggle-button")}
                    title={`Switch to ${monthInputTypes[index] === "variable" ? "date picker" : "variable input"}`}
                  />

                  {specificMonths.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="small"
                      icon="trash"
                      onClick={() => removeMonth(index)}
                      className={cx("remove-button")}
                    />
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="ghost"
                size="small"
                icon="plus"
                onClick={addMonth}
                className={cx("add-button")}
              >
                Add Month
              </Button>
            </div>
          </div>
        )}

        {monthMode === "range" && (
          <div className={cx("range-section")}>
            <label className={cx("section-label")}>Month Range</label>
            <p className={cx("range-description")}>
              Define start and end months. The system will automatically seed
              record values for all months between these bounds (inclusive).
            </p>
            <div className={cx("range-inputs")}>
              <div className={cx("range-input-container")}>
                <FormLabel
                  inputId="start_month"
                  label="Start Month"
                  isRequired
                />
                <DateSelectorInputBare
                  name="start_month"
                  variant="month"
                  value={yearMonthToDate(formValues.start_month ?? "")}
                  onChange={(date: DateValueOrRange | undefined) => {
                    if (typeof date === "undefined") return;
                    const singleDateValue =
                      date instanceof Date ? date : date.from;
                    handleRangeDateChange("start", singleDateValue);
                  }}
                  placeholder="Select start month"
                  className={cx("date-input")}
                  isRange={false}
                  isArraySyntax={false}
                  style={{ width: "100%" }}
                />
              </div>
              <div className={cx("range-input-container")}>
                <FormLabel
                  inputId="end_month"
                  label="End Month"
                  isRequired
                />
                <DateSelectorInputBare
                  name="end_month"
                  variant="month"
                  value={yearMonthToDate(formValues.end_month ?? "")}
                  onChange={(date) => {
                    if (typeof date === "undefined") return;
                    const singleDateValue =
                      date instanceof Date ? date : date.from;
                    handleRangeDateChange("end", singleDateValue);
                  }}
                  placeholder="Select end month"
                  className={cx("date-input")}
                  isRange={false}
                  isArraySyntax={false}
                  style={{ width: "100%" }}
                  min={yearMonthToDate(formValues.start_month ?? "")?.from}
                />
              </div>
            </div>
          </div>
        )}
      </Form>

      <div className={cx("filter-section")}>
        <div className={cx("filter-title")}>Filter Criteria</div>
        <div className={cx("filter-description")}>
          Define the criteria for which records should have default values
          applied
        </div>
        <FilterBuilder
          fields={filterFields}
          filter={filter}
          setFilter={setFilter}
          title="Record Filter"
          variant="vertical"
          showInputModeToggle
          availableVariables={availableVariables}
        />
      </div>
    </Skeleton>
  );
};

export default WorkflowConfigurationRunDRVCDetails;
